import client from '@/lib/axios-client'
import type {
	AssignCommitmentMatrixBody,
	AssignCommitmentMatrixExemptBody,
	CommitmentException,
	CommitmentExceptionListOutput,
	CommitmentExceptionListParams,
	CommitmentMatrix,
	CommitmentMatrixAssignmentListOutput,
	CommitmentMatrixAssignmentParams,
	CommitmentMatrixDashboardOutput,
	CommitmentMatrixDashboardParams,
	CommitmentMatrixEvolutionOutput,
	CommitmentMatrixEvolutionParams,
	CommitmentMatrixListOutput,
	CommitmentMatrixListParams,
	CommitmentMatrixSheetOutput,
	CommitmentMatrixSheetParams,
	CommitmentMatrixTargetHistoryOutput,
	CreateCommitmentExceptionBody,
	CreateCommitmentMatrixBody,
	MyCommitmentExceptionListParams,
	PersonTimelineOutput,
	PersonTimelineParams,
	UpdateCommitmentExceptionStatusBody,
	UpdateCommitmentMatrixBody,
} from '@/types'

const MATRICES_URL = '/commitment-matrices'
const EXCEPTIONS_URL = '/commitment-exceptions'
const PERSON_URL = '/person'

// ── Matrices ──────────────────────────────────────────────────────────────────

export async function getCommitmentMatrices(
	params?: CommitmentMatrixListParams,
): Promise<CommitmentMatrixListOutput> {
	const response = await client.get(MATRICES_URL, { params })
	return response.data.data
}

export async function createCommitmentMatrix(
	body: CreateCommitmentMatrixBody,
): Promise<CommitmentMatrix> {
	const response = await client.post(MATRICES_URL, body)
	return response.data.data
}

export async function updateCommitmentMatrix(
	id: string,
	body: UpdateCommitmentMatrixBody,
): Promise<CommitmentMatrix> {
	const response = await client.patch(`${MATRICES_URL}/${id}`, body)
	return response.data.data
}

export async function deleteCommitmentMatrix(id: string): Promise<void> {
	await client.delete(`${MATRICES_URL}/${id}`)
}

export async function getCommitmentMatrixTargetHistory(
	id: string,
): Promise<CommitmentMatrixTargetHistoryOutput> {
	const response = await client.get(`${MATRICES_URL}/${id}/target-history`)
	return response.data.data
}

// ── Assignment ────────────────────────────────────────────────────────────────

export async function getCommitmentMatrixAssignmentPeople(
	params?: CommitmentMatrixAssignmentParams,
): Promise<CommitmentMatrixAssignmentListOutput> {
	const response = await client.get(`${MATRICES_URL}/people`, { params })
	return response.data.data
}

export async function assignCommitmentMatrixToPerson(
	personId: string,
	body: AssignCommitmentMatrixBody,
): Promise<void> {
	await client.patch(`${PERSON_URL}/${personId}/commitment-matrix`, body)
}

export async function setCommitmentMatrixExemptForPerson(
	personId: string,
	body: AssignCommitmentMatrixExemptBody,
): Promise<void> {
	await client.patch(`${PERSON_URL}/${personId}/commitment-matrix-exempt`, body)
}

// ── Exceptions ────────────────────────────────────────────────────────────────

export async function getCommitmentExceptions(
	params?: CommitmentExceptionListParams,
): Promise<CommitmentExceptionListOutput> {
	const response = await client.get(EXCEPTIONS_URL, { params })
	return response.data.data
}

export async function getMyCommitmentExceptions(
	params?: MyCommitmentExceptionListParams,
): Promise<CommitmentExceptionListOutput> {
	const response = await client.get(`${EXCEPTIONS_URL}/me`, { params })
	return response.data.data
}

export async function createCommitmentException(
	body: CreateCommitmentExceptionBody,
): Promise<CommitmentException> {
	const response = await client.post(EXCEPTIONS_URL, body)
	return response.data.data
}

export async function updateCommitmentExceptionStatus(
	id: string,
	body: UpdateCommitmentExceptionStatusBody,
): Promise<CommitmentException> {
	const response = await client.patch(`${EXCEPTIONS_URL}/${id}/status`, body)
	return response.data.data
}

// activeBranchId é exigido para revogar um abono já aprovado
export async function deleteCommitmentException(
	id: string,
	activeBranchId?: string,
): Promise<void> {
	await client.delete(`${EXCEPTIONS_URL}/${id}`, {
		params: { activeBranchId },
	})
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export async function getCommitmentMatrixDashboard(
	params?: CommitmentMatrixDashboardParams,
): Promise<CommitmentMatrixDashboardOutput> {
	const response = await client.get(`${MATRICES_URL}/dashboard`, { params })
	return response.data.data
}

export async function getCommitmentMatrixEvolution(
	params?: CommitmentMatrixEvolutionParams,
): Promise<CommitmentMatrixEvolutionOutput> {
	const response = await client.get(`${MATRICES_URL}/dashboard/evolution`, {
		params,
	})
	return response.data.data
}

// ── Print sheet ───────────────────────────────────────────────────────────────

export async function getPersonCommitmentSheet(
	personId: string,
	params?: CommitmentMatrixSheetParams,
): Promise<CommitmentMatrixSheetOutput> {
	const response = await client.get(
		`${PERSON_URL}/${personId}/commitment-matrix-sheet`,
		{ params },
	)
	return response.data.data
}

// ── Person timeline ───────────────────────────────────────────────────────────

export async function getPersonTimeline(
	personId: string,
	params?: PersonTimelineParams,
): Promise<PersonTimelineOutput> {
	const response = await client.get(
		`${MATRICES_URL}/dashboard/person/${personId}/timeline`,
		{ params },
	)
	return response.data.data
}
